import { YOUTUBE_CONFIG } from '../config/constants';

/**
 * YouTube Service
 * Handles YouTube API interactions and player management
 */

class YouTubeService {
  constructor() {
    this.apiLoaded = false;
    this.apiReady = false;
    this.loadingPromise = null;
  }

  /**
   * Load the YouTube IFrame API
   * @returns {Promise<void>}
   */
  loadAPI() {
    // If already loaded or loading, return existing promise
    if (this.apiLoaded || this.loadingPromise) {
      return this.loadingPromise || Promise.resolve();
    }

    this.loadingPromise = new Promise((resolve, reject) => {
      // Check if API is already loaded
      if (window.YT && window.YT.Player) {
        this.apiLoaded = true;
        this.apiReady = true;
        resolve();
        return;
      }

      // Define the callback for when API is ready
      window.onYouTubeIframeAPIReady = () => {
        this.apiLoaded = true;
        this.apiReady = true;
        resolve();
      };

      // Load the API script if not already present
      if (!document.getElementById('youtube-api-script')) {
        try {
          const tag = document.createElement('script');
          tag.id = 'youtube-api-script';
          tag.src = 'https://www.youtube.com/iframe_api';
          tag.onerror = () => {
            reject(new Error('Failed to load YouTube API script'));
          };
          
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } catch (error) {
          reject(error);
        }
      }
    });

    return this.loadingPromise;
  }

  /**
   * Create a YouTube player
   * @param {string} elementId - The ID of the element to create the player in
   * @param {string} videoId - The YouTube video ID
   * @param {Object} options - Player options
   * @returns {Promise<YT.Player>}
   */
  async createPlayer(elementId, videoId, options = {}) {
    // Ensure API is loaded
    await this.loadAPI();

    return new Promise((resolve, reject) => {
      try {
        const player = new window.YT.Player(elementId, {
          videoId,
          height: options.height || YOUTUBE_CONFIG.PLAYER_HEIGHT,
          width: options.width || YOUTUBE_CONFIG.PLAYER_WIDTH,
          playerVars: options.playerVars || YOUTUBE_CONFIG.PLAYER_VARS,
          events: {
            onReady: (event) => {
              if (options.onReady) {
                options.onReady(event);
              }
              resolve(player);
            },
            onError: (event) => {
              console.error('YouTube player error:', event);
              if (options.onError) {
                options.onError(event);
              }
              reject(new Error(`YouTube player error: ${event.data}`));
            },
            ...options.events
          }
        });
      } catch (error) {
        console.error('Error creating YouTube player:', error);
        reject(error);
      }
    });
  }

  /**
   * Destroy a YouTube player
   * @param {YT.Player} player - The player instance to destroy
   */
  destroyPlayer(player) {
    if (player && typeof player.destroy === 'function') {
      try {
        player.destroy();
      } catch (error) {
        console.error('Error destroying YouTube player:', error);
      }
    }
  }

  /**
   * Extract video ID from various YouTube URL formats
   * @param {string} url - YouTube URL
   * @returns {string|null} Video ID or null if not found
   */
  extractVideoId(url) {
    if (!url) return null;

    const patterns = [
      /(?:youtu\.be\/)([^&\n?#]+)/,
      /(?:youtube\.com\/(?:.*v=|.*\/|.*vi=|.*embed\/))([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  /**
   * Fetch video metadata
   * @param {string} videoId - YouTube video ID
   * @param {string} apiKey - YouTube API key
   * @returns {Promise<Object>} Video metadata
   */
  async fetchVideoMetadata(videoId, apiKey) {
    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${apiKey}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.items && data.items.length > 0) {
        return {
          id: videoId,
          title: data.items[0].snippet.title,
          thumbnail: data.items[0].snippet.thumbnails.medium.url,
          description: data.items[0].snippet.description
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching video metadata:', error);
      return null;
    }
  }

  /**
   * Fetch playlist items
   * @param {string} playlistId - YouTube playlist ID
   * @param {string} apiKey - YouTube API key
   * @param {number} maxResults - Maximum number of results
   * @returns {Promise<Array>} Array of video items
   */
  async fetchPlaylistItems(playlistId, apiKey, maxResults = 50) {
    try {
      const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=${maxResults}&playlistId=${playlistId}&key=${apiKey}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.items || [];
    } catch (error) {
      console.error('Error fetching playlist items:', error);
      return [];
    }
  }

  /**
   * Check if YouTube API is ready
   * @returns {boolean}
   */
  isReady() {
    return this.apiReady && window.YT && window.YT.Player;
  }
}

// Export singleton instance
export default new YouTubeService();
